import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarPlus2, CalendarCheck2, UsersRound, X } from "lucide-react";
import { useAtomValue } from "jotai";
import { Button } from "@/components/ui/button";
import { GetAllExpeditionBoResponseDtoExpedition } from "@/types/api/api-types";
import { AssignExpeditionModal } from "@/components/backOffice/pendingExpeditions/assignExpedition.modal";
import { PickupDateModal } from "@/components/shared/modal/pickupDate.modal";
import { DeliveryFromDateModal } from "@/components/shared/modal/deliveryFromDate.modal";
import { userAtom } from "@/stores/authStore";

type Props = {
  selectedExpeditions: GetAllExpeditionBoResponseDtoExpedition[];
  onSuccess: () => void;
};

export const BulkActionsBar = ({ selectedExpeditions, onSuccess }: Props) => {
  const { t } = useTranslation("boExpeditions");
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [pickupModalOpen, setPickupModalOpen] = useState(false);
  const [deliveryModalOpen, setDeliveryModalOpen] = useState(false);
  const user = useAtomValue(userAtom);

  const isAdmin = user?.role === "ADMIN" || user?.role === "SUPERADMIN";
  const canUse = isAdmin || user?.role === "MEMBER";
  if (!canUse) return null;

  const count = selectedExpeditions.length;
  const hasSelection = count > 0;

  return (
    <div
      className={`fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 flex-wrap items-center gap-5 rounded-xl border border-gray-700 bg-gray-800 p-3.5 shadow-lg transition-all duration-300 sm:p-4 ${
        hasSelection ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-4 opacity-0"
      }`}
    >
      <p className="w-30 text-sm font-medium text-gray-300">
        {t("bulkActions.selectedCount", { count })}
      </p>
      <div className="flex flex-wrap gap-2">
        {isAdmin && (
          <Button
            size="sm"
            variant="outline"
            className="h-9 border-gray-600 bg-gray-700 text-sm text-white shadow-sm hover:bg-gray-600 hover:text-white"
            onClick={() => setAssignModalOpen(true)}
          >
            <UsersRound className="h-4 w-4" />
            {t("bulkActions.assign")}
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          className="h-9 border-gray-600 bg-gray-700 text-sm text-white shadow-sm hover:bg-gray-600 hover:text-white"
          onClick={() => setPickupModalOpen(true)}
        >
          <CalendarPlus2 className="h-4 w-4" />
          {t("bulkActions.postponePickup")}
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-9 border-gray-600 bg-gray-700 text-sm text-white shadow-sm hover:bg-gray-600 hover:text-white"
          onClick={() => setDeliveryModalOpen(true)}
        >
          <CalendarCheck2 className="h-4 w-4" />
          {t("bulkActions.setDeliveryFromDate")}
        </Button>
        <button
          type="button"
          onClick={onSuccess}
          title={t("bulkActions.deselectAll")}
          className="rounded-full p-1.5 text-gray-300 hover:bg-gray-700 hover:text-white"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {assignModalOpen && (
        <AssignExpeditionModal
          expeditionIds={selectedExpeditions.map((e) => e.id)}
          open={assignModalOpen}
          onClose={() => setAssignModalOpen(false)}
          onSuccess={onSuccess}
        />
      )}
      {pickupModalOpen && (
        <PickupDateModal
          expeditions={selectedExpeditions}
          open={pickupModalOpen}
          onClose={() => setPickupModalOpen(false)}
          onSuccess={onSuccess}
        />
      )}
      {deliveryModalOpen && (
        <DeliveryFromDateModal
          expeditions={selectedExpeditions}
          open={deliveryModalOpen}
          onClose={() => setDeliveryModalOpen(false)}
          onSuccess={onSuccess}
        />
      )}
    </div>
  );
};
